import { useState, useEffect } from 'react';
import axios from 'axios';
import { LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { TrendingUp, TrendingDown, AlertCircle, CheckCircle, Info, Zap } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Insights = ({ token }) => {
  const [monthlyTrends, setMonthlyTrends] = useState([]);
  const [categoryTrends, setCategoryTrends] = useState([]);
  const [insights, setInsights] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchInsightsData();
  }, []);

  const fetchInsightsData = async () => {
    try {
      const [trendsRes, categoryRes, insightsRes] = await Promise.all([
        axios.get(`${BACKEND_URL}/api/stats/monthly-trends`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BACKEND_URL}/api/stats/category-trends`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
        axios.get(`${BACKEND_URL}/api/stats/insights`, {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);

      setMonthlyTrends(trendsRes.data);
      setCategoryTrends(categoryRes.data);
      setInsights(insightsRes.data);
    } catch (error) {
      console.error('Error fetching insights:', error);
    } finally {
      setLoading(false);
    }
  };

  const getInsightIcon = (type) => {
    switch (type) {
      case 'warning':
      case 'alert':
        return <AlertCircle className="h-5 w-5" />;
      case 'success':
        return <CheckCircle className="h-5 w-5" />;
      case 'info':
        return <Info className="h-5 w-5" />;
      default:
        return <Zap className="h-5 w-5" />;
    }
  };

  const getInsightColor = (type) => {
    switch (type) {
      case 'warning':
      case 'alert':
        return 'border-red-200 bg-red-50 text-red-900';
      case 'success':
        return 'border-green-200 bg-green-50 text-green-900';
      default:
        return 'border-blue-200 bg-blue-50 text-blue-900';
    }
  };

  const getMonthLabel = (monthStr) => {
    try {
      const [year, month] = monthStr.split('-');
      const date = new Date(year, parseInt(month) - 1);
      return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
    } catch {
      return monthStr;
    }
  };

  const monthlyChartData = monthlyTrends.map(trend => ({
    month: getMonthLabel(trend.month),
    Income: trend.income,
    Expense: trend.expense,
    Balance: trend.balance,
  }));

  const topCategories = categoryTrends
    .map(cat => ({
      name: cat.category_name,
      total: cat.monthly_data.reduce((sum, m) => sum + m.amount, 0),
    }))
    .sort((a, b) => b.total - a.total)
    .slice(0, 5);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-state">
        <p className="font-body text-muted-foreground">Loading insights...</p>
      </div>
    );
  }

  const currentMonth = monthlyTrends[monthlyTrends.length - 1];
  const previousMonth = monthlyTrends[monthlyTrends.length - 2];

  const monthOverMonthChange = previousMonth
    ? ((currentMonth?.expense - previousMonth.expense) / previousMonth.expense) * 100
    : 0;

  return (
    <div className="space-y-6" data-testid="insights-page">
      <div>
        <h1 className="text-4xl md:text-5xl font-heading font-semibold tracking-tight text-foreground">
          Spending Insights
        </h1>
        <p className="mt-1 text-base font-body text-muted-foreground">
          Analyze trends and make smarter budget decisions
        </p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-card border border-border rounded-sm shadow-sm p-6" data-testid="current-month-card">
          <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2">
            This Month's Spending
          </p>
          <p className="text-3xl font-heading font-bold tracking-tighter text-foreground">
            ${(currentMonth?.expense || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </p>
          {previousMonth && (
            <div className="flex items-center gap-2 mt-2">
              {monthOverMonthChange > 0 ? (
                <TrendingUp className="h-4 w-4 text-red-500" />
              ) : (
                <TrendingDown className="h-4 w-4 text-green-500" />
              )}
              <span
                className={`text-sm font-mono ${
                  monthOverMonthChange > 0 ? 'text-red-600' : 'text-green-600'
                }`}
              >
                {Math.abs(monthOverMonthChange).toFixed(1)}% vs last month
              </span>
            </div>
          )}
        </div>

        <div className="bg-card border border-border rounded-sm shadow-sm p-6" data-testid="average-monthly-card">
          <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2">
            Average Monthly Spending
          </p>
          <p className="text-3xl font-heading font-bold tracking-tighter text-foreground">
            ${monthlyTrends.length > 0
              ? (
                  monthlyTrends.reduce((sum, m) => sum + m.expense, 0) / monthlyTrends.length
                ).toLocaleString('en-US', { minimumFractionDigits: 2 })
              : '0.00'}
          </p>
        </div>

        <div className="bg-card border border-border rounded-sm shadow-sm p-6" data-testid="transactions-trend-card">
          <p className="text-xs font-mono text-muted-foreground uppercase tracking-wider mb-2">
            Total Transactions
          </p>
          <p className="text-3xl font-heading font-bold tracking-tighter text-foreground">
            {monthlyTrends.reduce((sum, m) => sum + m.transaction_count, 0)}
          </p>
        </div>
      </div>

      {insights.length > 0 && (
        <div className="bg-card border border-border rounded-sm shadow-sm p-6" data-testid="insights-alerts">
          <h2 className="text-2xl font-heading font-medium mb-4 flex items-center gap-2">
            <Zap className="h-6 w-6 text-brand-gold" />
            Smart Insights
          </h2>
          <div className="space-y-3">
            {insights.map((insight, idx) => (
              <div
                key={idx}
                className={`flex items-start gap-3 p-4 border rounded-sm ${getInsightColor(insight.type)}`}
                data-testid="insight-item"
              >
                <div className="mt-0.5">{getInsightIcon(insight.type)}</div>
                <div className="flex-1">
                  <p className="font-body font-semibold">{insight.title}</p>
                  <p className="text-sm font-body mt-1">{insight.message}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="bg-card border border-border rounded-sm shadow-sm p-6" data-testid="monthly-trends-chart">
        <h2 className="text-2xl font-heading font-medium mb-4">Monthly Income & Expense Trends</h2>
        {monthlyChartData.length === 0 ? (
          <p className="text-center text-muted-foreground font-body py-12">
            No data available yet. Add transactions to see trends.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={350}>
            <LineChart data={monthlyChartData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
              <XAxis
                dataKey="month"
                stroke="#78716C"
                style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
              />
              <YAxis
                stroke="#78716C"
                style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
              />
              <Tooltip
                contentStyle={{
                  backgroundColor: 'white',
                  border: '1px solid #E7E5E4',
                  borderRadius: '0.125rem',
                  fontFamily: 'JetBrains Mono',
                }}
              />
              <Legend />
              <Line
                type="monotone"
                dataKey="Income"
                stroke="#10B981"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
              <Line
                type="monotone"
                dataKey="Expense"
                stroke="#EF4444"
                strokeWidth={2}
                dot={{ r: 4 }}
              />
            </LineChart>
          </ResponsiveContainer>
        )}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-card border border-border rounded-sm shadow-sm p-6" data-testid="top-categories-chart">
          <h2 className="text-2xl font-heading font-medium mb-4">Top Spending Categories</h2>
          {topCategories.length === 0 ? (
            <p className="text-center text-muted-foreground font-body py-12">No spending data</p>
          ) : (
            <ResponsiveContainer width="100%" height={300}>
              <BarChart data={topCategories} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E7E5E4" />
                <XAxis
                  type="number"
                  stroke="#78716C"
                  style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  stroke="#78716C"
                  style={{ fontFamily: 'JetBrains Mono', fontSize: '12px' }}
                  width={120}
                />
                <Tooltip
                  contentStyle={{
                    backgroundColor: 'white',
                    border: '1px solid #E7E5E4',
                    borderRadius: '0.125rem',
                    fontFamily: 'JetBrains Mono',
                  }}
                />
                <Bar dataKey="total" fill="#EF4444" />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-card border border-border rounded-sm shadow-sm p-6" data-testid="monthly-comparison">
          <h2 className="text-2xl font-heading font-medium mb-4">Month-over-Month Comparison</h2>
          {monthlyTrends.length < 2 ? (
            <p className="text-center text-muted-foreground font-body py-12">
              Add more transactions across different months to see comparison
            </p>
          ) : (
            <div className="space-y-4">
              {monthlyTrends.slice(-3).reverse().map((trend, idx) => {
                const prevTrend = monthlyTrends[monthlyTrends.indexOf(trend) - 1];
                const change = prevTrend
                  ? ((trend.expense - prevTrend.expense) / prevTrend.expense) * 100
                  : 0;

                return (
                  <div key={idx} className="border border-border rounded-sm p-4">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-mono text-sm text-muted-foreground">
                        {getMonthLabel(trend.month)}
                      </span>
                      {prevTrend && (
                        <span
                          className={`text-xs font-mono flex items-center gap-1 ${
                            change > 0 ? 'text-red-600' : 'text-green-600'
                          }`}
                        >
                          {change > 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                          {Math.abs(change).toFixed(1)}%
                        </span>
                      )}
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <p className="text-xs font-body text-muted-foreground">Income</p>
                        <p className="text-lg font-mono font-medium text-green-600">
                          ${trend.income.toLocaleString()}
                        </p>
                      </div>
                      <div>
                        <p className="text-xs font-body text-muted-foreground">Expense</p>
                        <p className="text-lg font-mono font-medium text-red-600">
                          ${trend.expense.toLocaleString()}
                        </p>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Insights;
